import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { usePreventScroll } from "@react-aria/overlays";
import { AHB_WITHDRAW_REASON } from "core/consts";
import { AHBWithdrawFormValues, ValueOf } from "core/types";
import { ConfirmationDialogProps } from "ds_legacy/components/ConfirmationDialog/useConfirmationDialog";
import RadioGroupV2 from "ds_legacy/components/RadioGroupV2";
import RSButton from "ds_legacy/components/RSButton";
import { ConnectedTextarea } from "ds_legacy/components/TextArea";
import { important, margin, padding } from "ds_legacy/materials/metrics";
import {
  FONT_SIZE_14,
  FONT_WEIGHT_BOLD,
  LINE_HEIGHT_20,
} from "ds_legacy/materials/typography";
import { useFocusElement } from "dsl/hooks/useFocusElement";
import {
  convertModelDefinition,
  FormWatcher,
  SimpleFormRenderProps,
  toJS,
  valueDef,
} from "react-forms-state/src";
import { useTranslations } from "translations";
import { useGetTranslateWithdrawReason } from "./useAHBMessageSection";

export type WithdrawMessageReasonModalProps = Pick<
  ConfirmationDialogProps<AHBWithdrawFormValues>,
  "onCancel" | "opened" | "onSubmit" | "id"
>;

export function WithdrawMessageReasonModal({
  id,
  onCancel,
  onSubmit,
  opened,
}: WithdrawMessageReasonModalProps) {
  usePreventScroll({ isDisabled: !opened });
  const focusElement = useFocusElement<HTMLDivElement>({
    fallbackRestoreRef: document.querySelector("h1")!,
    focusFirstInnerElement: true,
    restoreFocus: true,
    setTabIndexFallbackRestoreRef: true,
  });
  const translations = useTranslations();
  const getTranslateWithdrawReason = useGetTranslateWithdrawReason();

  function handleClose() {
    onCancel?.();
  }

  return (
    <SimpleFormRenderProps
      asHtmlForm
      modelDefinition={convertModelDefinition({
        ...valueDef("withdraw_reason", {
          validate(value) {
            return value
              ? true
              : {
                  customMessage:
                    translations.patientForms.withdrawReasonValidation,
                };
          },
        }),
        ...valueDef("withdraw_reason_other", {
          convertOut(value) {
            return typeof toJS<string | null>(value) === "string"
              ? value.trim()
              : value;
          },
          validate(value, _, global) {
            const { withdraw_reason } = toJS<{
              withdraw_reason: ValueOf<typeof AHB_WITHDRAW_REASON>;
            }>(global);

            if (withdraw_reason === AHB_WITHDRAW_REASON.OTHER && !value) {
              return {
                customMessage:
                  translations.insuranceApp.eligibilityTab.sendDocuments
                    .withdrawModal.validationOther,
              };
            }

            return true;
          },
        }),
      })}
      onSubmit={onSubmit}
    >
      {({ submit }) => (
        <Dialog
          ref={focusElement as any}
          data-testid={id}
          fullWidth
          id={id}
          open={opened}
          PaperProps={{
            "aria-modal": "true",
            sx: {
              gap: margin(2),
              padding: important(padding(3, 1)),
            },
          }}
          onClose={handleClose}
        >
          <DialogTitle sx={{ padding: padding(0, 3) }}>
            {
              translations.insuranceApp.eligibilityTab.sendDocuments
                .withdrawModal.withdrawTitle
            }
          </DialogTitle>
          <DialogContent sx={{ padding: padding(0, 3) }}>
            <RadioGroupV2
              elementName="withdraw_reason"
              formControlLabelSx={{ marginLeft: margin(0) }}
              formLabelSx={{
                fontSize: FONT_SIZE_14,
                fontWeight: FONT_WEIGHT_BOLD,
                lineHeight: LINE_HEIGHT_20,
              }}
              label={
                translations.insuranceApp.eligibilityTab.sendDocuments
                  .withdrawModal.withdrawSubtitle
              }
              options={[
                {
                  label: getTranslateWithdrawReason(
                    AHB_WITHDRAW_REASON.PATIENT_DECLINED_REHAB,
                  ),
                  value: AHB_WITHDRAW_REASON.PATIENT_DECLINED_REHAB,
                },
                {
                  label: getTranslateWithdrawReason(
                    AHB_WITHDRAW_REASON.NOT_REHAB_CAPABLE,
                  ),
                  value: AHB_WITHDRAW_REASON.NOT_REHAB_CAPABLE,
                },
                {
                  label: getTranslateWithdrawReason(
                    AHB_WITHDRAW_REASON.NOT_PROMISING,
                  ),
                  value: AHB_WITHDRAW_REASON.NOT_PROMISING,
                },
                {
                  label: getTranslateWithdrawReason(
                    AHB_WITHDRAW_REASON.PATIENT_DECEASED,
                  ),
                  value: AHB_WITHDRAW_REASON.PATIENT_DECEASED,
                },
                {
                  label: getTranslateWithdrawReason(AHB_WITHDRAW_REASON.OTHER),
                  subForm: (
                    <ConnectedTextarea
                      characterLimit={600}
                      elementName="withdraw_reason_other"
                      flatModel
                      fullWidth
                      label={
                        translations.insuranceApp.eligibilityTab.sendDocuments
                          .withdrawModal.otherPlaceholder
                      }
                      marginOverride={margin(0)}
                      placeholder={
                        translations.insuranceApp.eligibilityTab.sendDocuments
                          .withdrawModal.otherPlaceholder
                      }
                      required
                      rows={3}
                    />
                  ),
                  subFormStyle: {
                    margin: margin(0, 0, 0, 4),
                  },
                  value: AHB_WITHDRAW_REASON.OTHER,
                },
              ]}
              radioGroupSx={{
                display: "flex",
                flexDirection: "column",
                gap: margin(1.5),
                marginTop: margin(1.5),
              }}
              sideMutation={(newValue, mutate) =>
                mutate("", "withdraw_reason_other")
              }
            />
          </DialogContent>
          <FormWatcher watchPath="withdraw_reason">
            {({ watchedValue: withdraw_reason }) => (
              <DialogActions sx={{ gap: margin(3), padding: padding(0, 2) }}>
                <RSButton
                  buttonWrapperStyle={{ margin: margin(0) }}
                  color="primary"
                  id="cancel-withdrawal"
                  loading="na"
                  style={{ margin: margin(0) }}
                  trackEventProps={{ withdraw_reason }}
                  variant="outlined"
                  onClick={handleClose}
                >
                  {translations.actions.cancel}
                </RSButton>
                <RSButton
                  buttonWrapperStyle={{ margin: margin(0) }}
                  color="primary"
                  id="confirm-withdrawal"
                  loading="na"
                  style={{ margin: margin(0) }}
                  trackEventProps={{ withdraw_reason }}
                  type="submit"
                  variant="contained"
                  onClick={submit}
                >
                  {translations.actions.confirm}
                </RSButton>
              </DialogActions>
            )}
          </FormWatcher>
        </Dialog>
      )}
    </SimpleFormRenderProps>
  );
}
